import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, ArrowUpRight, BookOpen, Sparkles } from 'lucide-react';
import GithubIcon from './icons/GithubIcon';
import SpotlightCard from './SpotlightCard';
import { projectsData, projectCategories } from '../data/projects';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100 } }
};

const getCategoryTheme = (cat) => {
  if (cat.includes("Desktop")) {
    return {
      badge: "bg-sky-500/10 border-sky-500/30 text-sky-400",
      spotlight: "rgba(56, 189, 248, 0.16)",
      dot: "bg-sky-400",
      metric: "text-sky-400"
    };
  }
  if (cat.includes(".NET") || cat.includes("C#")) {
    return {
      badge: "bg-indigo-500/10 border-indigo-500/30 text-indigo-400",
      spotlight: "rgba(99, 102, 241, 0.16)",
      dot: "bg-indigo-400",
      metric: "text-indigo-400"
    };
  }
  return {
    badge: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
    spotlight: "rgba(16, 185, 129, 0.16)",
    dot: "bg-emerald-400",
    metric: "text-emerald-400"
  };
};

const ProjectCard = ({ project, onOpenCaseStudy }) => {
  const { title, description, tags, githubLink, demoLink, category, metrics } = project;
  const theme = getCategoryTheme(category);

  return (
    <motion.div 
      layout
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.35 }}
      className="h-full"
    >
      <SpotlightCard
        tiltIntensity={7}
        spotlightColor={theme.spotlight}
        className="h-full bg-white/60 dark:bg-[#111]/60 backdrop-blur-xl rounded-[32px] p-8 md:p-10 border border-slate-200/60 dark:border-[#222] hover:border-slate-500/40 hover:shadow-2xl transition-all duration-500 flex flex-col justify-between"
      >
        <div className="flex-1 flex flex-col">
          {/* Top Header: Category Tag & Arrow */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-2">
              <span className={`px-3.5 py-1 border text-xs font-semibold uppercase tracking-wider rounded-full shadow-sm ${theme.badge}`}>
                {category}
              </span>
            </div>

            <button
              onClick={() => onOpenCaseStudy(project)}
              aria-label={`Open case study for ${title}`}
              className="w-11 h-11 rounded-full flex items-center justify-center bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200/50 dark:border-[#333] text-slate-500 dark:text-slate-400 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-all duration-300 group/btn"
            >
              <ArrowUpRight size={18} className="group-hover/btn:rotate-45 transition-transform duration-300" />
            </button>
          </div>

          {/* Title */}
          <h3 className="text-2xl md:text-3xl font-bold font-display tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-slate-900 to-slate-500 dark:from-white dark:to-slate-300 pb-1 mb-3">
            {title}
          </h3>
          
          {/* Description */}
          <p className="text-slate-600 dark:text-slate-400 mb-8 font-light leading-relaxed text-base md:text-lg">
            {description}
          </p>

          {/* Metric Pill if available */}
          {metrics && metrics.length > 0 && (
            <div className="mb-6 py-2 px-4 rounded-xl bg-slate-50/70 dark:bg-[#161616]/70 border border-slate-200/50 dark:border-[#262626] flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400 font-medium">Highlight:</span>
              <span className={`font-mono font-semibold ${theme.metric}`}>
                {metrics[0].value} {metrics[0].label}
              </span>
            </div>
          )}

          <div className="mt-auto">
            {/* Tech Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-3 py-1.5 bg-slate-50 dark:bg-[#1A1A1A] border border-slate-200/60 dark:border-[#333] text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider rounded-xl cursor-default flex items-center gap-1.5 shadow-sm"
                >
                  <span className={`w-1 h-1 rounded-full ${theme.dot}`}></span>
                  {tag}
                </span>
              ))}
            </div>

            {/* Action Row */}
            <div className="flex items-center justify-between pt-6 border-t border-slate-100 dark:border-[#222]">
              <button
                onClick={() => onOpenCaseStudy(project)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white hover:opacity-80 transition-opacity"
              >
                <BookOpen size={16} />
                Read Case Study
              </button>

              <div className="flex items-center gap-4">
                <a 
                  href={githubLink || "https://github.com/RSanjeyan"} 
                  target="_blank" 
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-black dark:text-slate-400 dark:hover:text-white transition-colors"
                >
                  <GithubIcon size={14} />
                  Code
                </a>
                {demoLink && demoLink !== '#' && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-slate-300 dark:bg-[#333]"></div>
                    <a 
                      href={demoLink} 
                      target="_blank" 
                      rel="noreferrer"
                      className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-black dark:text-slate-400 dark:hover:text-white transition-colors"
                    >
                      <ExternalLink size={14} />
                      Live
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </SpotlightCard>
    </motion.div>
  );
};

const Projects = ({ onSelectProject }) => {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredProjects = selectedCategory === "All"
    ? projectsData
    : projectsData.filter(p => p.category.toLowerCase().includes(selectedCategory.toLowerCase()));

  return (
    <section id="projects" className="py-32 bg-background dark:bg-background-dark">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-[#181818] border border-slate-200 dark:border-[#2B2B2B] text-slate-600 dark:text-slate-400 text-xs font-mono font-medium mb-4">
                <Sparkles size={13} className="text-emerald-500" />
                Featured Architecture & Engineering
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-4 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-500 dark:from-white dark:to-slate-400 pb-1">
                Selected Work.
              </h2>
              <div className="w-20 h-1.5 bg-gradient-to-r from-sky-400 via-indigo-500 to-emerald-400 rounded-full shadow-sm shadow-indigo-500/20"></div>
            </div>

            <p className="text-slate-500 dark:text-slate-400 max-w-sm text-sm font-medium">
              Click any project card to inspect its technical architecture, problem statement, and engineering metrics.
            </p>
          </div>

          {/* Category Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2 p-1.5 bg-slate-100/70 dark:bg-[#111] border border-slate-200 dark:border-[#222] rounded-2xl w-fit">
            {projectCategories.map((category) => {
              const count = category === "All" 
                ? projectsData.length 
                : projectsData.filter(p => p.category.toLowerCase().includes(category.toLowerCase())).length;
              const isActive = selectedCategory === category;

              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`relative px-4 py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors duration-200 z-10 flex items-center gap-2 ${
                    isActive
                      ? 'text-white dark:text-black font-semibold'
                      : 'text-slate-600 dark:text-slate-400 hover:text-black dark:hover:text-white'
                  }`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeProjectCategory"
                      className="absolute inset-0 bg-black dark:bg-white rounded-xl shadow-sm -z-10"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <span>{category}</span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                    isActive 
                      ? 'bg-white/20 dark:bg-black/20 text-white dark:text-black' 
                      : 'bg-slate-200 dark:bg-[#222] text-slate-500'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Project Grid */}
        <motion.div 
          layout
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                onOpenCaseStudy={onSelectProject}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
